import React, { useEffect, useMemo, useState } from 'react'
import { ChevronDown, ChevronRight, Monitor, MapPin } from 'lucide-react'
import { inputClass } from './io/ui'

/**
 * Live Broadsign inventory as Site (venue) → Faces (screens).
 */
export default function InventoryPicker({
  selectedIds = [],
  onChange,
  venueUid,
  required = false,
  mode = 'select',
  title = 'Inventory',
  subtitle = 'Sites and faces from live Control display units',
}) {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [configured, setConfigured] = useState(true)
  const [sites, setSites] = useState([])
  const [criteria, setCriteria] = useState([])
  const [q, setQ] = useState('')
  const [andCriteria, setAndCriteria] = useState('')
  const [open, setOpen] = useState({})

  const [noInventoryForVenue, setNoInventoryForVenue] = useState(false)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      setLoading(true)
      setError('')
      try {
        const params = new URLSearchParams()
        if (q) params.set('q', q)
        if (venueUid) params.set('venueUid', venueUid)
        if (andCriteria) params.set('andCriteriaIds', andCriteria)
        const r = await fetch(`/api/io/inventory?${params.toString()}`, { credentials: 'include' })
        const data = await r.json().catch(() => ({}))
        if (!r.ok) throw new Error(data.error || 'Screen inventory could not be loaded right now. Please try again shortly or contact support.')
        if (cancelled) return
        setConfigured(data.configured !== false)
        const nextSites = Array.isArray(data.sites) && data.sites.length
          ? data.sites
          : groupFallback(data.displayUnits || [])
        setSites(nextSites)
        // An empty result has two very different causes and users cannot tell them
        // apart from "No sites match this filter". Either this venue has no screens
        // in Broadsign Control at all (the common one while the DU push is still in
        // progress - see CNTXT-157), or the search box / criteria filter excluded
        // everything. Remember which, so the empty state can say the right thing.
        // Diagnosed 2026-09-14: a distributor was stuck on step 2 for exactly this
        // reason and it took a database dig to establish the venue simply had no
        // display units pushed yet.
        setNoInventoryForVenue(!nextSites.length && !q && !andCriteria)
        setCriteria(data.criteria || [])
        setOpen((prev) => {
          const next = { ...prev }
          nextSites.forEach((s, i) => {
            if (next[s.siteId] == null) next[s.siteId] = i < 8
          })
          return next
        })
      } catch (e) {
        if (!cancelled) setError(e.message || String(e))
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [q, venueUid, andCriteria])

  // A screen is identified by its Control display-unit id when it has one, and by
  // its Onboarding device NAME when it does not (migration window, 2026-09-15 -
  // see listOnboardingDisplays in lib/ioRoutes.js). Selections therefore carry a
  // mix of numbers and strings; the API splits them, and flattenDuIds discards the
  // strings so no Broadsign reservation payload can be built from a screen that
  // does not exist in Control. Number(null) is 0, so keying these on u.id would
  // have collapsed every Onboarding screen onto a single checkbox.
  const unitKey = (u) => (u?.id == null ? String(u?.deviceName || u?.name || '') : Number(u.id))
  const keyOf = (v) => (typeof v === 'string' && !Number.isFinite(Number(v)) ? v : Number(v))

  const selectedSet = useMemo(() => new Set(selectedIds.map(keyOf)), [selectedIds])

  const toggleFace = (u) => {
    if (mode !== 'select' || !onChange) return
    const k = unitKey(u)
    if (!k && k !== 0) return
    const next = selectedSet.has(k)
      ? selectedIds.filter((x) => keyOf(x) !== k)
      : [...selectedIds, k]
    onChange(next)
  }

  const toggleSite = (site) => {
    if (mode !== 'select' || !onChange) return
    const keys = (site.faces || []).map(unitKey).filter((k) => k || k === 0)
    const allOn = keys.length > 0 && keys.every((k) => selectedSet.has(k))
    if (allOn) onChange(selectedIds.filter((x) => !keys.includes(keyOf(x))))
    else onChange([...new Set([...selectedIds.map(keyOf), ...keys])])
  }

  return (
    <div className="px-1 py-2">
      <div className="flex items-center space-x-2 mb-4">
        <Monitor className="w-5 h-5 text-blue-600 dark:text-blue-400" />
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">{title}</h2>
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {subtitle}{required ? ' *' : ''}
        </span>
      </div>

      {!configured && (
        <p className="text-sm text-amber-700 dark:text-amber-300 mb-3">
          Screen booking is not connected in this environment. You can still save a
          draft; ops will traffic it after approval.
        </p>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
        <input
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search sites, screens, city…"
          className={inputClass}
        />
        <select
          value={andCriteria}
          onChange={(e) => setAndCriteria(e.target.value)}
          className={inputClass}
        >
          <option value="">Any criteria</option>
          {criteria.map((c) => (
            <option key={c.id} value={String(c.id)}>{c.name}</option>
          ))}
        </select>
      </div>

      {loading && <p className="text-sm text-gray-500">Loading live inventory…</p>}
      {error && <p className="text-sm text-red-600">{error}</p>}
      {!loading && !error && !sites.length && configured && (
        noInventoryForVenue ? (
          <div className="text-sm text-amber-700 dark:text-amber-300">
            <p className="font-medium">No screens are on file for this venue.</p>
            <p className="mt-1 text-gray-600 dark:text-gray-400">
              Nothing is recorded against it yet, so there is nothing to select. Contact
              Context Networks and we will get the venue&apos;s screens loaded.
            </p>
          </div>
        ) : (
          <p className="text-sm text-gray-500">
            No screens match your search or criteria filter. Clear them to see everything at this venue.
          </p>
        )
      )}

      <div className="space-y-2 max-h-[28rem] overflow-y-auto">
        {sites.map((site) => {
          const expanded = !!open[site.siteId]
          const faceIds = (site.faces || []).map((f) => Number(f.id))
          const selectedHere = faceIds.filter((id) => selectedSet.has(id)).length
          return (
            <div key={site.siteId} className="border border-gray-200 dark:border-gray-700 rounded-lg">
              <div className="flex items-center gap-2 px-3 py-2">
                <button
                  type="button"
                  className="text-gray-500"
                  onClick={() => setOpen((p) => ({ ...p, [site.siteId]: !expanded }))}
                  aria-label={expanded ? 'Collapse site' : 'Expand site'}
                >
                  {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                </button>
                {mode === 'select' && (
                  <input
                    type="checkbox"
                    checked={faceIds.length > 0 && selectedHere === faceIds.length}
                    onChange={() => toggleSite(site)}
                  />
                )}
                <MapPin className="w-4 h-4 text-blue-500 shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                    {site.venueUid ? `${site.venueUid} · ${site.name}` : site.name}
                  </div>
                  <div className="text-xs text-gray-500">
                    {[site.city, site.state].filter(Boolean).join(', ') || 'No city'}
                    {` · ${site.faces?.length || 0} face(s)`}
                    {mode === 'select' && selectedHere ? ` · ${selectedHere} selected` : ''}
                  </div>
                </div>
              </div>
              {expanded && (
                <div className="border-t border-gray-200 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-700">
                  {(site.faces || []).map((u) => (
                    <label
                      key={unitKey(u)}
                      className={`flex items-start gap-3 px-4 py-2 ${mode === 'select' ? 'cursor-pointer' : ''} ${
                        selectedSet.has(unitKey(u))
                          ? 'bg-blue-50 dark:bg-blue-900/20'
                          : ''
                      }`}
                    >
                      {mode === 'select' && (
                        <input
                          type="checkbox"
                          className="mt-1"
                          checked={selectedSet.has(unitKey(u))}
                          onChange={() => toggleFace(u)}
                        />
                      )}
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {u.faceNumber || u.deviceName || u.name}
                          {u.inControl === false && (
                            <span className="ml-2 align-middle rounded px-1.5 py-0.5 text-[10px] font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
                              pending migration
                            </span>
                          )}
                        </div>
                        {/* No `|| u.name` here: the bold line above already
                            shows the name, and repeating it filled this line
                            with the same text twice. Built as parts so the
                            separators stay right when a screen has no
                            description. */}
                        <div className="text-xs text-gray-500">
                          {[
                            u.faceDescription || null,
                            u.resolutionWidth && u.resolutionHeight
                              ? `${u.resolutionWidth}×${u.resolutionHeight}`
                              : null,
                            u.id != null ? `DU ${u.id}` : 'awaiting migration',
                          ].filter(Boolean).join(' · ')}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>
          )
        })}
      </div>

      {mode === 'select' && selectedIds.length > 0 && (
        <p className="text-xs text-gray-500 mt-2">{selectedIds.length} face(s) selected</p>
      )}
    </div>
  )
}

function groupFallback(units) {
  const map = new Map()
  for (const u of units) {
    const key = u.venueUid || '_unassigned'
    if (!map.has(key)) {
      map.set(key, {
        siteId: key,
        venueUid: u.venueUid || null,
        name: u.venueName || (key === '_unassigned' ? 'Unassigned screens' : u.address) || 'Site',
        city: u.city,
        state: u.state,
        faces: [],
      })
    }
    map.get(key).faces.push(u)
  }
  return [...map.values()]
}
